import { create } from 'zustand'
import { DEFAULT_DOCK_WIDTH } from '@/lib/assistant-dock'

/**
 * Assistant dock open state and width, remembered per browser user (same
 * pattern as the bid menu). Closed on first visit. The stored width is the
 * user's preference; `clampDockWidth` fits it to the window at render time.
 */
export const ASSISTANT_DOCK_OPEN_KEY = 'bidcontext:assistant-dock:open'
export const ASSISTANT_DOCK_WIDTH_KEY = 'bidcontext:assistant-dock:width'

function readOpen(): boolean {
  try {
    return localStorage.getItem(ASSISTANT_DOCK_OPEN_KEY) === 'true'
  } catch {
    return false
  }
}

function readWidth(): number {
  try {
    const parsed = parseInt(localStorage.getItem(ASSISTANT_DOCK_WIDTH_KEY) ?? '', 10)
    return Number.isFinite(parsed) ? parsed : DEFAULT_DOCK_WIDTH
  } catch {
    return DEFAULT_DOCK_WIDTH
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Not persisted; the in-memory state still applies for this session.
  }
}

interface AssistantDockState {
  isOpen: boolean
  width: number
  setOpen: (isOpen: boolean) => void
  toggle: () => void
  setWidth: (width: number) => void
  /** Re-read storage (tests, and another tab having changed it). */
  hydrate: () => void
}

export const useAssistantDockStore = create<AssistantDockState>((set, get) => ({
  isOpen: readOpen(),
  width: readWidth(),
  setOpen: (isOpen) => {
    write(ASSISTANT_DOCK_OPEN_KEY, String(isOpen))
    set({ isOpen })
  },
  toggle: () => get().setOpen(!get().isOpen),
  setWidth: (width) => {
    const rounded = Math.round(width)
    write(ASSISTANT_DOCK_WIDTH_KEY, String(rounded))
    set({ width: rounded })
  },
  hydrate: () => set({ isOpen: readOpen(), width: readWidth() }),
}))
